import { z } from "zod";
/** Locales that have their own seeded demo account. */
export const DEMO_LOCALES = ["pl", "en", "de"];
/** Public wedding site handle for the dev demo account. */
export const DEMO_HANDLE = "demo";
export const DEMO_HANDLE_EN = "demo-en";
export const DEMO_HANDLE_DE = "demo-de";
/** Per-locale public wedding site handle. */
export function demoHandleForLocale(locale) {
    if (locale === "en")
        return DEMO_HANDLE_EN;
    if (locale === "de")
        return DEMO_HANDLE_DE;
    return DEMO_HANDLE;
}
/** Fixed couple names for the anonymized dev demo wedding. */
export const DEMO_COUPLE_NAME1 = "Romeo";
export const DEMO_COUPLE_NAME2 = "Giulietta";
export function demoCoupleDisplayName() {
    return `${DEMO_COUPLE_NAME1} & ${DEMO_COUPLE_NAME2}`;
}
/** Days until wedding day — keeps countdown at ~11 months after daily reset. */
export const DEMO_COUNTDOWN_DAYS = 330;
/** Storage path for the canonical demo wedding snapshot JSON. */
export const DEMO_SNAPSHOT_STORAGE_PATH = "system/demo/snapshot.json";
/** Per-locale snapshot storage paths. */
export function demoSnapshotPathForLocale(locale) {
    if (locale === "en")
        return "system/demo/snapshot-en.json";
    if (locale === "de")
        return "system/demo/snapshot-de.json";
    return DEMO_SNAPSHOT_STORAGE_PATH;
}
/** Firestore metadata doc id under `/system/demo`. */
export const DEMO_META_DOC_PATH = "system/demo";
/** Per-locale Firestore metadata doc paths. */
export function demoMetaDocPathForLocale(locale) {
    if (locale === "en")
        return "system/demo-en";
    if (locale === "de")
        return "system/demo-de";
    return DEMO_META_DOC_PATH;
}
export const DemoMetaSchema = z.object({
    demoUid: z.string(),
    demoWeddingId: z.string(),
    handle: z.string(),
    sourceEmail: z.string().email(),
    snapshotPath: z.string(),
    countdownDays: z.number().int().positive().default(DEMO_COUNTDOWN_DAYS),
    lastResetAt: z.string().datetime().nullable().optional(),
});
/**
 * UTC midnight on the wedding date, or null when missing/invalid.
 *
 * Used as a TZ-independent floor for delta arithmetic in the demo
 * reset flow — not for user-facing countdown display (the web app has
 * its own local-midnight `weddingDayStartMs` in `features/website`).
 */
export function weddingDayStartMs(weddingDate) {
    if (weddingDate == null)
        return null;
    let ms;
    if (typeof weddingDate === "string") {
        ms = Date.parse(weddingDate);
    }
    else if (weddingDate instanceof Date) {
        ms = weddingDate.getTime();
    }
    else if (typeof weddingDate === "number") {
        ms = weddingDate;
    }
    else {
        const ts = weddingDate;
        if (typeof ts.toMillis === "function")
            ms = ts.toMillis();
        else if (typeof ts.toDate === "function")
            ms = ts.toDate().getTime();
        else if (typeof ts.seconds === "number")
            ms = ts.seconds * 1000;
        else if (typeof ts._seconds === "number")
            ms = ts._seconds * 1000;
        else
            return null;
    }
    if (!Number.isFinite(ms) || ms <= 0)
        return null;
    return new Date(ms).setUTCHours(0, 0, 0, 0);
}
/** Start of calendar day in Europe/Warsaw as UTC epoch ms. */
export function startOfDayWarsawMs(now = new Date()) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Warsaw",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(now);
    const year = Number(parts.find((p) => p.type === "year")?.value);
    const month = Number(parts.find((p) => p.type === "month")?.value);
    const day = Number(parts.find((p) => p.type === "day")?.value);
    // Warsaw midnight as ISO date-only, interpreted in Warsaw via offset lookup.
    const probe = new Date(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`);
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "Europe/Warsaw",
        timeZoneName: "shortOffset",
        hour: "numeric",
    });
    const tzPart = formatter.formatToParts(probe).find((p) => p.type === "timeZoneName")?.value ?? "GMT+1";
    const match = tzPart.match(/GMT([+-]\d+(?::\d+)?)/);
    const offset = match?.[1] ?? "+1";
    const normalized = offset.includes(":") ? offset : `${offset}:00`;
    const sign = normalized.startsWith("-") ? "-" : "+";
    const [h, m = "0"] = normalized.replace(/^[+-]/, "").split(":");
    const offsetMs = (Number(h) * 60 + Number(m)) * 60_000 * (sign === "-" ? -1 : 1);
    return Date.UTC(year, month - 1, day) - offsetMs;
}
/** Target wedding-day midnight (local) so countdown stays at `countdownDays`. */
export function computeDemoWeddingDateMs(snapshotWeddingDate, countdownDays = DEMO_COUNTDOWN_DAYS, now = new Date()) {
    const todayWarsawMs = startOfDayWarsawMs(now);
    const targetWeddingMs = todayWarsawMs + countdownDays * 86_400_000;
    const snapshotMs = weddingDayStartMs(snapshotWeddingDate);
    if (snapshotMs == null)
        return targetWeddingMs;
    return targetWeddingMs;
}
/** Delta to apply to snapshot ISO datetimes so wedding lands on target day. */
export function computeDemoDateDeltaMs(snapshotWeddingDate, countdownDays = DEMO_COUNTDOWN_DAYS, now = new Date()) {
    const targetWeddingMs = computeDemoWeddingDateMs(snapshotWeddingDate, countdownDays, now);
    const snapshotMs = weddingDayStartMs(snapshotWeddingDate);
    if (snapshotMs == null)
        return 0;
    return targetWeddingMs - snapshotMs;
}
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
/** Shift ISO datetime strings in a nested object graph by `deltaMs`. */
export function shiftIsoDates(value, deltaMs) {
    if (deltaMs === 0)
        return value;
    return shiftIsoDatesInner(value, deltaMs);
}
function shiftIsoDatesInner(value, deltaMs) {
    if (value == null)
        return value;
    if (typeof value === "string" && ISO_DATETIME_RE.test(value)) {
        const shifted = new Date(Date.parse(value) + deltaMs);
        return Number.isFinite(shifted.getTime()) ? shifted.toISOString() : value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => shiftIsoDatesInner(item, deltaMs));
    }
    if (typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = shiftIsoDatesInner(v, deltaMs);
        }
        return out;
    }
    return value;
}
